import React, { useMemo, useState } from 'react';
import {
  BookOpen,
  Brain,
  ChevronDown,
  CheckCircle,
  Loader2,
  RefreshCw,
  Target,
  Trophy,
  XCircle,
  Zap,
} from 'lucide-react';
import { quizBankQuestions, type QuizBankQuestion } from '../data/quizBank';
import { stemsData } from '../data/stems';
import { doorsData } from '../data/doors';
import { starsData } from '../data/stars';
import { godsData } from '../data/gods';
import { trigramsData } from '../data/trigrams';
import { SymbolData } from '../types';
import XuanYuanTopBar from './XuanYuanTopBar';

interface QuizProps {
  onBack: () => void;
  intro?: string;
}

type SectionId = 'imagery' | 'theory' | 'practice' | 'all' | 'wrongBook';
type Step = 'setup' | 'playing' | 'result';
type QuestionCount = 10 | 20 | 30 | 50 | 100;

interface QuizQuestion {
  id: string;
  category: string;
  text: string;
  options: string[];
  correctAnswers: number[];
  explanation: string;
}

interface WrongAnswer {
  questionId: string;
  question: string;
  category: string;
  userAnswer: string;
  correctAnswer: string;
  wrongCount: number;
  lastWrongAt: number;
}

interface QuizRecord {
  id: string;
  section: SectionId;
  questionCount: number;
  score: number;
  percentage: number;
  timeSpent: number;
  createdAt: number;
}

interface QuizStats {
  totalQuizzes: number;
  totalQuestions: number;
  totalCorrect: number;
  totalWrong: number;
  accuracyRate: number;
  streakDays: number;
  lastQuizAt: number | null;
}

const SECTION_LABEL: Record<SectionId, string> = {
  imagery: '象意测试',
  theory: '理论知识',
  practice: '实战检测',
  all: '知识大乱斗',
  wrongBook: '错题本练习',
};

const EXAM_OPTIONS: Array<{ id: SectionId; label: string; desc: string }> = [
  { id: 'imagery', label: '象意测试', desc: '按符号本身象意出题（如甲=大树、金玉）。' },
  { id: 'theory', label: '理论知识', desc: '原有抽象知识与规则题。' },
  { id: 'practice', label: '实战检测', desc: '偏综合判断与应用场景。' },
  { id: 'all', label: '知识大乱斗', desc: '象意 + 理论 + 实战混合抽题。' },
];

interface ImageryGroup {
  symbolChar: string;
  symbolSummary: string;
  label: string;
  options: string[];
}

const XIANGYI_SOURCE_SYMBOLS: SymbolData[] = [
  ...stemsData,
  ...doorsData,
  ...starsData,
  ...godsData,
  ...trigramsData,
];

const QUIZ_HISTORY_KEY = 'qimen_quiz_history_v1';
const QUIZ_WRONG_BOOK_KEY = 'qimen_quiz_wrong_book_v1';
const QUIZ_STATS_KEY = 'qimen_quiz_stats_v1';
const MAX_HISTORY_RECORDS = 50;
const MAX_WRONG_BOOK_ITEMS = 200;

function loadQuizHistory(): QuizRecord[] {
  try {
    const stored = localStorage.getItem(QUIZ_HISTORY_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_HISTORY_RECORDS) : [];
  } catch {
    return [];
  }
}

function saveQuizHistory(record: QuizRecord): void {
  try {
    const history = loadQuizHistory();
    const updated = [record, ...history].slice(0, MAX_HISTORY_RECORDS);
    localStorage.setItem(QUIZ_HISTORY_KEY, JSON.stringify(updated));
  } catch {
    // ignore
  }
}

function loadWrongBook(): WrongAnswer[] {
  try {
    const stored = localStorage.getItem(QUIZ_WRONG_BOOK_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    return Array.isArray(parsed) ? parsed.slice(0, MAX_WRONG_BOOK_ITEMS) : [];
  } catch {
    return [];
  }
}

function saveWrongBook(items: WrongAnswer[]): void {
  try {
    localStorage.setItem(QUIZ_WRONG_BOOK_KEY, JSON.stringify(items.slice(0, MAX_WRONG_BOOK_ITEMS)));
  } catch {
    // ignore
  }
}

function addToWrongBook(item: Omit<WrongAnswer, 'wrongCount' | 'lastWrongAt'>): void {
  const book = loadWrongBook();
  const existing = book.find((x) => x.questionId === item.questionId);
  if (existing) {
    existing.wrongCount += 1;
    existing.lastWrongAt = Date.now();
    existing.userAnswer = item.userAnswer;
  } else {
    book.push({
      ...item,
      wrongCount: 1,
      lastWrongAt: Date.now(),
    });
  }
  saveWrongBook(book);
}

function removeFromWrongBook(questionId: string): void {
  const book = loadWrongBook();
  const filtered = book.filter((x) => x.questionId !== questionId);
  saveWrongBook(filtered);
}

function loadQuizStats(): QuizStats {
  try {
    const stored = localStorage.getItem(QUIZ_STATS_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
  } catch {
    // ignore
  }
  return {
    totalQuizzes: 0,
    totalQuestions: 0,
    totalCorrect: 0,
    totalWrong: 0,
    accuracyRate: 0,
    streakDays: 0,
    lastQuizAt: null,
  };
}

function saveQuizStats(stats: QuizStats): void {
  try {
    localStorage.setItem(QUIZ_STATS_KEY, JSON.stringify(stats));
  } catch {
    // ignore
  }
}

function updateStatsAfterQuiz(score: number, total: number): QuizStats {
  const stats = loadQuizStats();
  const wrong = total - score;
  stats.totalQuizzes += 1;
  stats.totalQuestions += total;
  stats.totalCorrect += score;
  stats.totalWrong += wrong;
  stats.accuracyRate = stats.totalQuestions > 0
    ? Math.round((stats.totalCorrect / stats.totalQuestions) * 100)
    : 0;

  const now = Date.now();
  const lastDate = stats.lastQuizAt ? new Date(stats.lastQuizAt).toDateString() : null;
  const today = new Date(now).toDateString();
  if (lastDate !== today) {
    const yesterday = new Date(now - 86400000).toDateString();
    stats.streakDays = lastDate === yesterday ? stats.streakDays + 1 : 1;
  }
  stats.lastQuizAt = now;
  saveQuizStats(stats);
  return stats;
}

function shuffle<T>(arr: T[]): T[] {
  const next = [...arr];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function normalizeBankQuestion(q: QuizBankQuestion): QuizQuestion {
  const options = Array.isArray(q.options) ? q.options.map(String).slice(0, 4) : [];
  const firstValid = Array.isArray(q.correctAnswers)
    ? q.correctAnswers.map((x) => Number(x)).find((n) => Number.isInteger(n) && n >= 0 && n <= 3)
    : undefined;
  const answer = typeof firstValid === 'number' ? firstValid : 0;
  return {
    id: String(q.id || ''),
    category: String(q.category || SECTION_LABEL[q.section as SectionId] || ''),
    text: String(q.text || '').replace(/第\s*\d+\s*题/g, '').trim(),
    options,
    correctAnswers: [answer],
    explanation: String(q.explanation || '暂无解析'),
  };
}

function normalizeOptionText(raw: string): string {
  return String(raw || '')
    .replace(/[（(][^）)]*[）)]/g, '')
    .replace(/[“”"']/g, '')
    .replace(/^\s*[正负][:：]\s*/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitImageryOptionCandidates(raw: string): string[] {
  const source = String(raw || '')
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n');
  const parts = source
    .split(/[\n、，。；;：:]+/g)
    .map((x) => normalizeOptionText(x))
    .filter(Boolean)
    .map((x) => x.replace(/[（(][^）)]*[）)]/g, '').trim())
    .filter((x) => x.length >= 2 && x.length <= 20);
  return Array.from(new Set(parts));
}

function deterministicRotate<T>(arr: T[], seed: number): T[] {
  if (arr.length <= 1) return [...arr];
  const start = Math.abs(seed) % arr.length;
  return [...arr.slice(start), ...arr.slice(0, start)];
}

function collectImageryGroups(symbols: SymbolData[]): ImageryGroup[] {
  const map = new Map<string, ImageryGroup>();
  for (const symbol of symbols) {
    for (const origin of symbol.coreOrigins || []) {
      for (const sub of origin.subItems || []) {
        const label = String(sub?.label || '').trim();
        if (!label) continue;
        const candidates = splitImageryOptionCandidates(sub?.content || '');
        if (!candidates.length) continue;
        const key = `${symbol.char}::${label}`;
        if (!map.has(key)) {
          map.set(key, {
            symbolChar: symbol.char,
            symbolSummary: symbol.summary || symbol.char,
            label,
            options: [],
          });
        }
        const row = map.get(key)!;
        row.options = Array.from(new Set([...row.options, ...candidates])).slice(0, 8);
      }
    }
  }
  return Array.from(map.values()).filter((x) => x.options.length > 0);
}

function buildImageryQuestionBank(targetCount = 100): QuizQuestion[] {
  const groups = collectImageryGroups(XIANGYI_SOURCE_SYMBOLS);
  const labelMap = new Map<string, string[]>();
  const globalPool = Array.from(
    new Set(groups.flatMap((group) => group.options)),
  );
  groups.forEach((group) => {
    if (!labelMap.has(group.label)) labelMap.set(group.label, []);
    const merged = Array.from(new Set([...(labelMap.get(group.label) || []), ...group.options]));
    labelMap.set(group.label, merged);
  });

  const questions: QuizQuestion[] = [];
  for (const group of groups) {
    const correct = group.options[0];
    if (!correct) continue;
    const sameLabelPool = (labelMap.get(group.label) || [])
      .filter((x) => x !== correct);
    const crossLabelPool = globalPool.filter((x) => x !== correct);
    const seed = (group.symbolChar.charCodeAt(0) || 0) + group.label.length * 11 + group.options.length * 7;

    let distractors = deterministicRotate(sameLabelPool, seed).slice(0, 3);
    if (distractors.length < 3) {
      const supplement = deterministicRotate(crossLabelPool, seed + 17)
        .filter((x) => !distractors.includes(x))
        .slice(0, 3 - distractors.length);
      distractors = [...distractors, ...supplement];
    }
    const optionSet = Array.from(new Set([correct, ...distractors])).slice(0, 4);
    if (optionSet.length < 4) continue;
    const options = deterministicRotate(optionSet, seed + 23);
    const correctIndex = options.findIndex((x) => x === correct);
    if (correctIndex < 0) continue;

    questions.push({
      id: `XIANGYI-${String(questions.length + 1).padStart(3, '0')}`,
      category: `${group.symbolChar} · ${group.label}`,
      text: `下列哪一项更符合「${group.symbolChar}」在「${group.label}」中的象意？`,
      options,
      correctAnswers: [correctIndex],
      explanation: `「${group.symbolChar}」在「${group.label}」象意中，更准确的是「${correct}」。`,
    });
    if (questions.length >= targetCount) break;
  }
  return questions.slice(0, targetCount);
}

function normalizeQuestionSection(q: QuizBankQuestion): Exclude<SectionId, 'all'> {
  if (q.section === 'imagery') return 'theory';
  return q.section === 'practice' ? 'practice' : 'theory';
}

const IMAGERY_QUESTION_BANK: QuizQuestion[] = buildImageryQuestionBank(100);

const BANKS: Record<Exclude<SectionId, 'all' | 'wrongBook'>, QuizQuestion[]> = (() => {
  const base: Record<Exclude<SectionId, 'all' | 'wrongBook'>, QuizQuestion[]> = {
    imagery: IMAGERY_QUESTION_BANK,
    theory: [],
    practice: [],
  };
  quizBankQuestions.forEach((item) => {
    const normalized = normalizeBankQuestion(item);
    if (!normalized.text || normalized.options.length !== 4) return;
    const section = normalizeQuestionSection(item);
    base[section].push(normalized);
  });
  return base;
})();

function pickRandomQuestions(section: SectionId, count: QuestionCount, wrongBookItems?: WrongAnswer[]): QuizQuestion[] {
  if (section === 'wrongBook' && wrongBookItems && wrongBookItems.length > 0) {
    const allQuestions = [...BANKS.imagery, ...BANKS.theory, ...BANKS.practice];
    const wrongQuestionIds = new Set(wrongBookItems.map((x) => x.questionId));
    const matchedQuestions = allQuestions.filter((q) => wrongQuestionIds.has(q.id));
    const safeCount = Math.min(Math.max(1, Number(count) || 10), matchedQuestions.length || 1);
    return shuffle(matchedQuestions).slice(0, safeCount);
  }
  if (section === 'all') {
    const pool: QuizQuestion[] = [...BANKS.imagery, ...BANKS.theory, ...BANKS.practice];
    const safeCount = Math.min(Math.max(1, Number(count) || 10), pool.length || 1);
    return shuffle(pool).slice(0, safeCount);
  }
  const pool: QuizQuestion[] = BANKS[section as keyof typeof BANKS] || [];
  const safeCount = Math.min(Math.max(1, Number(count) || 10), pool.length || 1);
  return shuffle(pool).slice(0, safeCount);
}

const Quiz: React.FC<QuizProps> = ({ onBack, intro }) => {
  const [section, setSection] = useState<SectionId>('imagery');
  const [showSectionPicker, setShowSectionPicker] = useState(false);
  const [step, setStep] = useState<Step>('setup');
  const [isLoading, setIsLoading] = useState(false);

  const [questionCount, setQuestionCount] = useState<QuestionCount>(10);
  const [questions, setQuestions] = useState<QuizQuestion[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedAnswers, setSelectedAnswers] = useState<number[]>([]);
  const [score, setScore] = useState(0);
  const [showExplanation, setShowExplanation] = useState(false);
  const [wrongAnswers, setWrongAnswers] = useState<WrongAnswer[]>([]);
  const [analysisReport, setAnalysisReport] = useState('');

  const [quizStats, setQuizStats] = useState<QuizStats>(() => loadQuizStats());
  const [wrongBook, setWrongBook] = useState<WrongAnswer[]>(() => loadWrongBook());
  const [quizStartTime, setQuizStartTime] = useState<number>(0);

const selectedExamOption = useMemo(
    () => EXAM_OPTIONS.find((x) => x.id === section) || EXAM_OPTIONS[0],
    [section],
  );
  const availableLocalCount = useMemo(() => {
    if (section === 'wrongBook') return wrongBook.length;
    if (section === 'all') return BANKS.imagery.length + BANKS.theory.length + BANKS.practice.length;
    return BANKS[section]?.length || 0;
  }, [section, wrongBook.length]);
  const sectionDesc = useMemo(() => {
    if (section === 'imagery') {
      return `当前为象意专项题库（${BANKS.imagery.length} 题），聚焦"符号代表什么"。`;
    }
    if (section === 'all') {
      return `当前为综合乱斗题库（${availableLocalCount} 题），包含象意+理论+实战。`;
    }
    if (section === 'wrongBook') {
      return `当前错题本共 ${wrongBook.length} 题，可针对性练习。`;
    }
    return `当前题库可用 ${availableLocalCount} 题。`;
  }, [section, availableLocalCount, wrongBook.length]);

  const buildLocalAnalysis = (finalQuestions: QuizQuestion[], finalScore: number, wrongs: WrongAnswer[]) => {
    const total = finalQuestions.length || 1;
    const percent = Math.round((finalScore / total) * 100);
    const wrongByCategory = wrongs.reduce<Record<string, number>>((acc, item) => {
      acc[item.category] = (acc[item.category] || 0) + 1;
      return acc;
    }, {});
    const topWeak = Object.entries(wrongByCategory).sort((a, b) => b[1] - a[1]).slice(0, 3);
    const weakLines = topWeak.length
      ? topWeak.map(([k, v]) => `- ${k}：错 ${v} 题`)
      : ['- 当前错题分布较均衡，建议继续提升稳定性。'];

    return [
      '### 总评',
      `当前成绩：${finalScore}/${total}（${percent}%）。`,
      '### 薄弱点分析',
      ...weakLines,
      '### 训练建议',
      '- 先复盘错题，再做同领域 10 题巩固。',
      '- 做题时优先抓取用神、四害、门星神关系。',
      '- 每周至少做 1 次 30 题综合练习。',
    ].join('\n');
  };

  const startQuiz = async () => {
    setIsLoading(true);
    setWrongAnswers([]);
    setAnalysisReport('');
    setScore(0);
    setCurrentIndex(0);
    setSelectedAnswers([]);
    setShowExplanation(false);

    try {
      const localQuestions = pickRandomQuestions(section, questionCount);
      if (!localQuestions.length) {
        throw new Error('本地题库为空，请切换考试内容后重试');
      }
      setQuestions(localQuestions);

      setStep('playing');
    } catch (err) {
      const msg = err instanceof Error ? err.message : '出题失败';
      alert(msg === '会员已过期' ? '会员已过期或权限不足，请联系开通。' : msg);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmAnswer = () => {
    const currentQ = questions[currentIndex];
    const isCorrect = JSON.stringify([...selectedAnswers].sort()) === JSON.stringify([...currentQ.correctAnswers].sort());

    if (isCorrect) {
      setScore((s) => s + 1);
      if (section === 'wrongBook') {
        removeFromWrongBook(currentQ.id);
        setWrongBook(loadWrongBook());
      }
    } else {
      const wrongItem: WrongAnswer = {
        questionId: currentQ.id,
        question: currentQ.text,
        category: currentQ.category,
        userAnswer: selectedAnswers.map((i) => currentQ.options[i]).join(', '),
        correctAnswer: currentQ.correctAnswers.map((i) => currentQ.options[i]).join(', '),
        wrongCount: 1,
        lastWrongAt: Date.now(),
      };
      setWrongAnswers((prev) => [...prev, wrongItem]);
      addToWrongBook(wrongItem);
      setWrongBook(loadWrongBook());
    }
    setShowExplanation(true);
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex((c) => c + 1);
      setSelectedAnswers([]);
      setShowExplanation(false);
      return;
    }
    const timeSpent = Date.now() - quizStartTime;
    const percentage = questions.length ? Math.round((score / questions.length) * 100) : 0;
    const record: QuizRecord = {
      id: `quiz_${Date.now()}`,
      section,
      questionCount: questions.length,
      score,
      percentage,
      timeSpent,
      createdAt: Date.now(),
    };
    saveQuizHistory(record);
    const newStats = updateStatsAfterQuiz(score, questions.length);
    setQuizStats(newStats);
    setStep('result');
    setAnalysisReport(buildLocalAnalysis(questions, score, wrongAnswers));
  };

  const renderMarkdown = (text: string) => {
    return text.split('\n').map((line, idx) => {
      if (line.startsWith('###')) return <h3 key={idx} className="text-lg font-bold text-slate-800 mt-4 mb-2">{line.replace('###', '')}</h3>;
      if (line.startsWith('-')) return <li key={idx} className="ml-4 text-slate-600 mb-1">{line.replace('-', '')}</li>;
      return <p key={idx} className="text-slate-600 mb-2 leading-relaxed">{line}</p>;
    });
  };

  if (step === 'setup') {
    return (
      <div className="h-full min-h-0 bg-slate-50 flex flex-col">
        <XuanYuanTopBar
          title="轩辕国学·习题训练"
          onBack={onBack}
          subtitle={intro}
          variant="light"
        />
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="bg-white p-8 rounded-3xl shadow-xl text-center max-w-2xl w-full border border-slate-100">
            <div className="w-20 h-20 bg-teal-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <Target className="w-10 h-10 text-teal-600" />
            </div>
            <h2 className="text-2xl font-bold mb-2 text-slate-800">习题训练中心</h2>
            <p className="text-slate-500 mb-6">{sectionDesc}</p>

            <div className="mb-6 text-left">
              <p className="text-xs font-bold text-slate-500 mb-2 tracking-wide">考试内容</p>
              <button
                onClick={() => setShowSectionPicker((prev) => !prev)}
                className="w-full flex items-center justify-between rounded-xl border-2 border-teal-200 bg-teal-50 px-4 py-3 text-teal-800 font-bold"
              >
                <span>{selectedExamOption.label}</span>
                <ChevronDown className={`w-4 h-4 transition-transform ${showSectionPicker ? 'rotate-180' : ''}`} />
              </button>
              {showSectionPicker && (
                <div className="mt-2 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100">
                  {EXAM_OPTIONS.map((item) => (
                    <button
                      key={item.id}
                      onClick={() => {
                        setSection(item.id);
                        setShowSectionPicker(false);
                      }}
                      className={`w-full px-4 py-3 text-left transition-colors ${
                        section === item.id ? 'bg-teal-50' : 'hover:bg-slate-50'
                      }`}
                    >
                      <p className={`text-sm font-semibold ${section === item.id ? 'text-teal-700' : 'text-slate-700'}`}>{item.label}</p>
                      <p className="text-xs text-slate-500 mt-1">{item.desc}</p>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-5 gap-3 mb-8">
              {[10, 20, 30, 50, 100].map((count) => (
                <button
                  key={count}
                  onClick={() => setQuestionCount(count as QuestionCount)}
                  className={`py-2.5 rounded-xl font-bold border-2 transition-all text-sm ${
                    questionCount === count ? 'border-teal-500 bg-teal-50 text-teal-700' : 'border-slate-200 text-slate-400 hover:border-teal-200'
                  }`}
                >
                  {count} 题
                </button>
              ))}
            </div>

            <button
              onClick={startQuiz}
              disabled={isLoading}
              className="w-full py-4 bg-teal-600 hover:bg-teal-700 text-white rounded-xl font-bold shadow-lg shadow-teal-200 flex justify-center items-center gap-2 transition-colors disabled:bg-slate-300 disabled:shadow-none"
            >
              {isLoading ? <Loader2 className="animate-spin w-5 h-5" /> : <RefreshCw className="w-5 h-5" />}
              {isLoading ? `正在准备 ${questionCount} 道题目...` : '开始训练'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'result') {
    const percentage = questions.length ? Math.round((score / questions.length) * 100) : 0;
    const weakTop = (Object.entries(
      wrongAnswers.reduce<Record<string, number>>((acc, item) => {
        acc[item.category] = (acc[item.category] || 0) + 1;
        return acc;
      }, {}),
    ) as Array<[string, number]>)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([k, v]) => `${k}(${v})`)
      .join('、') || '暂无明显弱项';

    return (
      <div className="h-full min-h-0 bg-slate-50 flex flex-col">
        <XuanYuanTopBar
          title="轩辕国学·习题训练报告"
          onBack={() => setStep('setup')}
          variant="light"
        />
        <div className="flex-1 overflow-y-auto p-4 lg:p-8">
          <div className="max-w-2xl mx-auto space-y-6">
            <div className="bg-white p-8 rounded-3xl shadow-sm border border-slate-200 text-center relative overflow-hidden">
              <div className="relative z-10">
                <Trophy className={`w-16 h-16 mx-auto mb-4 ${percentage >= 80 ? 'text-amber-400' : 'text-slate-300'}`} />
                <h2 className="text-lg font-bold text-slate-500 uppercase tracking-widest mb-1">最终得分</h2>
                <div className="text-6xl font-black text-slate-800 mb-2">{score} <span className="text-2xl text-slate-400 font-medium">/ {questions.length}</span></div>
                <div className={`inline-block px-4 py-1 rounded-full text-sm font-bold ${percentage >= 60 ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}`}>
                  {percentage >= 90 ? '优秀' : percentage >= 60 ? '合格' : '需强化'}
                </div>
              </div>
              <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-teal-400 to-indigo-500" />
            </div>

            <div className="bg-white p-6 rounded-3xl border border-slate-200">
              <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2"><Zap className="w-5 h-5 text-amber-500" />薄弱点概览</h3>
              <p className="text-slate-700">{weakTop}</p>
            </div>

            <div className="bg-white p-6 lg:p-8 rounded-3xl shadow-sm border border-slate-200">
              <h3 className="text-xl font-bold text-slate-800 mb-6 flex items-center gap-2">
                <BookOpen className="w-6 h-6 text-indigo-500" />
                学习建议
              </h3>
              <div className="prose prose-slate max-w-none">
                {analysisReport ? renderMarkdown(analysisReport) : <p className="text-slate-500">暂无分析报告</p>}
              </div>
            </div>

            <button onClick={() => setStep('setup')} className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold hover:bg-slate-800 transition-colors shadow-lg">
              再练一次
            </button>
          </div>
        </div>
      </div>
    );
  }

  const q = questions[currentIndex];
  if (!q) return null;

  const progress = ((currentIndex + 1) / questions.length) * 100;

  return (
    <div className="h-full min-h-0 bg-slate-50 flex flex-col">
      <XuanYuanTopBar
        title="轩辕国学·习题训练"
        onBack={onBack}
        variant="light"
      />
      <div className="bg-white px-4 py-3 border-b border-slate-200 flex justify-center items-center sticky top-0 z-10 shadow-sm">
        <div className="flex flex-col items-center">
          <span className="font-bold text-slate-800">第 {currentIndex + 1} / {questions.length} 题</span>
          <span className="text-[10px] text-slate-400 font-medium px-2 py-0.5 bg-slate-100 rounded-full mt-1">{q.category}</span>
        </div>

        <div className="absolute bottom-0 left-0 w-full h-1 bg-slate-100">
          <div className="h-full bg-teal-500 transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-2xl mx-auto w-full pb-20">
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm mb-6">
            <h3 className="text-lg md:text-xl font-bold text-slate-800 leading-relaxed">{q.text}</h3>
          </div>

          <div className="space-y-3">
            {q.options.map((opt: string, i: number) => {
              let stateStyles = 'border-slate-200 bg-white hover:border-indigo-300 hover:bg-slate-50';
              if (selectedAnswers.includes(i)) {
                stateStyles = 'border-indigo-600 bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600';
              }
              if (showExplanation) {
                if (q.correctAnswers.includes(i)) {
                  stateStyles = 'border-emerald-500 bg-emerald-50 text-emerald-700 ring-1 ring-emerald-500';
                } else if (selectedAnswers.includes(i)) {
                  stateStyles = 'border-rose-500 bg-rose-50 text-rose-700 ring-1 ring-rose-500';
                } else {
                  stateStyles = 'border-slate-200 bg-white opacity-50';
                }
              }

              return (
                <button
                  key={i}
                  disabled={showExplanation}
                  onClick={() => !showExplanation && setSelectedAnswers([i])}
                  className={`w-full p-4 rounded-xl border-2 text-left transition-all font-medium flex items-center justify-between group ${stateStyles}`}
                >
                  <span>{opt}</span>
                  {showExplanation && q.correctAnswers.includes(i) && <CheckCircle className="w-5 h-5 text-emerald-600" />}
                  {showExplanation && selectedAnswers.includes(i) && !q.correctAnswers.includes(i) && <XCircle className="w-5 h-5 text-rose-600" />}
                </button>
              );
            })}
          </div>

          <div className="mt-8">
            {!showExplanation ? (
              <button
                onClick={handleConfirmAnswer}
                disabled={selectedAnswers.length === 0}
                className="w-full py-4 bg-slate-900 text-white rounded-xl font-bold disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:bg-slate-800 transition-all"
              >
                确认答案
              </button>
            ) : (
              <div className="space-y-6 animate-fade-in-up">
                <div className="p-5 bg-indigo-50 rounded-2xl border border-indigo-100">
                  <h4 className="font-bold text-indigo-900 mb-2 flex items-center gap-2">
                    <BookOpen className="w-4 h-4" /> 题目解析
                  </h4>
                  <p className="text-sm text-indigo-800 leading-relaxed">{q.explanation}</p>
                </div>
                <button
                  onClick={handleNext}
                  className="w-full py-4 bg-teal-600 text-white rounded-xl font-bold shadow-lg hover:bg-teal-700 transition-all"
                >
                  {currentIndex < questions.length - 1 ? '下一题' : '查看结果'}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Quiz;
